import { motion } from "framer-motion";
import { useState, useEffect, useCallback, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Play,
  TrendingUp,
  Zap,
  Users,
  BarChart3,
  Headphones,
  ChevronDown,
  Mic,
  Brain,
  GraduationCap,
  Megaphone,
  Menu, X
} from "lucide-react";
import { getCustomerPortalSession, getPlanList } from "../redux/actions";
import { useDispatch, useSelector } from "react-redux";
import { PricingPlanModal } from "../components/PricingPlanModal";
import { DistributionModal } from "../components/DistributionAccess/DistributionModal";
import { generateMetadata } from "../seo/metadataGenerator";
import { isPrerendering } from "../helper/isPrerendering";
import { PAGE_OG_IMAGES } from "../seo/constants";
import { getFeatureHashPath } from "../seo/featureModules";
import { generateFAQPageSchema, generateGraphSchema, generateKnowledgeGraphSchema, generateModuleSchema, generateOrganizationSchema, generateSoftwareApplicationSchema, generateWebsiteSchema } from "../seo/schemaGenerator";
import SeoHead from "../seo/SeoHead";
import { usePrerenderReady } from "../hooks/usePrerenderReady";

export default function Home() {
  usePrerenderReady();
  const { state } = useLocation();
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const [showDistributionModal, setShowDistributionModal] = useState(false);
  const [cursorPosition, setCursorPosition] = useState({ x: -100, y: -100 });
  const [isHovering, setIsHovering] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoad, setIsLoad] = useState(false)
  const [showPricingPlanModal, setShowPricingPlanModal] = useState(false);
  const { hasActiveSubscription, subscriptionPlan } = useSelector((state) => state.billing);
  const planList = subscriptionPlan || [];
  const lastSubscriptionFetched = subscriptionPlan?.lastFetched;
  const token = localStorage.getItem("authToken");

  const updateCursorPosition = useCallback((e) => {
    requestAnimationFrame(() => {
      setCursorPosition({ x: e.clientX, y: e.clientY });
    });
  }, []);

  const handleShowDistribution = () => {
    setShowDistributionModal(!showDistributionModal)
  }

  useEffect(() => {
    if (state?.scrollToBottom) {
      window.scrollTo({
        top: document.body.scrollHeight,
        behavior: "smooth"
      });
    }
  }, [state]);

  useEffect(() => {
    document.body.style.cursor = 'none';
    const handleMouseEnter = () => setIsHovering(true);
    const handleMouseLeave = () => setIsHovering(false);

    const interactiveElements = document.querySelectorAll(
      'a, button, input, [role="button"]'
    );
    interactiveElements.forEach((element) => {
      element.addEventListener("mouseenter", handleMouseEnter);
      element.addEventListener("mouseleave", handleMouseLeave);
    });

    window.addEventListener("mousemove", updateCursorPosition);

    // Simulate loading time (remove this in production and use real loading logic)
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 3000);

    return () => {
      clearTimeout(timer);
      document.body.style.cursor = 'auto';
      window.removeEventListener("mousemove", updateCursorPosition);
      interactiveElements.forEach((element) => {
        element.removeEventListener("mouseenter", handleMouseEnter);
        element.removeEventListener("mouseleave", handleMouseLeave);
      });
    };
  }, [updateCursorPosition]);

  const hasFetched = useRef(false);
  useEffect(() => {
    const fetchPlans = async () => {
      const shouldFetch =
        !planList?.length ||
        !lastSubscriptionFetched ||
        Date.now() - lastSubscriptionFetched > 1000 * 60 * 30; // 30 mins cache

      if (shouldFetch && !hasFetched.current && !isPrerendering() && !!token) {
        hasFetched.current = true; // prevent duplicate fetches
        await dispatch(getPlanList("subscription"));
      }
    };

    fetchPlans();

    // reset flag when component unmounts or on logout, if needed
    return () => {
      hasFetched.current = false;
    };
  }, [dispatch, planList?.length, lastSubscriptionFetched]);

  const handleSubscription = async () => {
    if (token) {
      try {
        setIsLoad(true); // 🔹 start loading
        if (hasActiveSubscription) {
          await dispatch(getCustomerPortalSession());
        } else {
          setShowPricingPlanModal(true);
        }
      } catch (err) {
        console.error('Error handling Pro button:', err);
      } finally {
        setIsLoad(false); // 🔹 stop loading
      }
    } else {
      navigate('/signup')
    }
  }

  const navigateToFeatures = (id) => {
    navigate(getFeatureHashPath(id))
  }

  const metadata = generateMetadata({
    title:
      "UpNComer | The Operating System for Artists and Labels",

    description:
      "UpNComer helps independent artists manage every aspect of their music career, from campaigns to distribution, all in one place, so you can focus on making music while we help handle the rest.",

    image: PAGE_OG_IMAGES.home,

    path: "/",
  })

  const schemas = generateGraphSchema([
    generateOrganizationSchema(),
    generateWebsiteSchema(),
    generateSoftwareApplicationSchema(),
    generateModuleSchema(),
    generateKnowledgeGraphSchema(),
    generateFAQPageSchema()
  ])

  return (
    <>
      {/* {isLoading && <HomepageLoader />} */}
      <SeoHead
        metadata={metadata}
        schemas={schemas}
      />
      <div
        className={`cursor-spotlight ${isHovering ? "hovering" : ""}`}
        style={{
          left: `${cursorPosition.x}px`,
          top: `${cursorPosition.y}px`,
          transform: `translate(-50%, -50%)`,
        }}
      >
        <div className="inner-circle"></div>
      </div>
      {/* <div
        className={`transition-all duration-1000 ${isLoading ? "opacity-0 blur-xl" : "opacity-100 blur-none"
          }`}
      >  */}
      <div className="relative min-h-screen bg-gradient-to-br from-[#000456] via-[#00067E] to-[#1e3a8a] overflow-hidden">
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-gradient-to-br from-[#000322] via-[#00067E] to-[#3b82f6] opacity-90" />
          <motion.div
            animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.6, 0.3] }}
            transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
            className="absolute top-20 right-20 w-96 h-96 bg-gradient-to-br from-[#6366f1]/20 via-[#4f46e5]/15 to-[#FA6138]/10 rounded-full blur-3xl"
          />
        </div>

        {/* Main Content */}
        <main className="relative z-10 mt-16 md:mt-18 py-10 md:py-12 lg:py-14">
          <div className="container mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center">
              {/* Enhanced Left Content */}
              <div className="max-w-2xl w-full mx-auto">
                <motion.div
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 1, ease: "easeOut" }}
                  className="space-y-6 xl:space-y-10"
                >
                  {/* Refined Typography Section with Dynamic Blue Gradients */}
                  <div className="space-y-5 lg:space-y-8">
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.8, delay: 0.2 }}
                      className="text-xs md:text-sm tracking-[0.3em] bg-gradient-to-r from-[#c7d2fe]/70 via-[#a5b4fc]/80 to-[#818cf8]/70 bg-clip-text text-transparent font-light"
                    >
                      DRIVEN{" "}
                      <span className="text-[#FA6138] font-normal">
                        BY ARTISTS, FOR ARTISTS
                      </span>
                    </motion.div>

                    {/* <motion.h1
                      initial={{ opacity: 0, y: 30 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 1, delay: 0.3 }}
                      className="space-y-2 xl:space-y-3 font-archivo"
                    // className="space-y-2 xl:space-y-3 font-['Arial_Black',Arial,sans-serif]"
                    >
                      <div className="text-4xl lg:text-5xl xl:text-7xl font-bold tracking-tight leading-none">
                        <span className="inline-block transform -skew-x-6 bg-gradient-to-r from-[#c7d2fe] to-[#a5b4fc] bg-clip-text text-transparent drop-shadow-lg xl:px-2">
                          THE OPERATING
                        </span>
                        <span className="inline-block transform skew-x-6 ml-3 bg-gradient-to-r from-[#818cf8] to-[#6366f1] bg-clip-text text-transparent drop-shadow-lg xl:px-2">
                          SYSTEM FOR
                        </span>
                      </div>
                      <div className="text-4xl lg:text-5xl xl:text-7xl font-bold tracking-tight leading-none">
                        <span className="inline-block transform -skew-x-6 bg-gradient-to-r from-[#FA6138] to-[#e55532] bg-clip-text text-transparent drop-shadow-lg xl:px-2"
                          style={{
                            filter:
                              "drop-shadow(0 0 30px rgba(250, 97, 56, 0.5))",
                          }}
                        >
                          INDEPENDENT
                        </span>
                        <span
                          className="inline-block transform skew-x-6 ml-3 bg-gradient-to-r from-[#e55532] to-[#FA6138] bg-clip-text text-transparent drop-shadow-lg py-0 xl:px-2"
                          style={{
                            filter:
                              "drop-shadow(0 0 30px rgba(250, 97, 56, 0.5))",
                          }}
                        >
                          MUSIC
                        </span>
                      </div>
                    </motion.h1> */}

                    <motion.h1
                      initial={{ opacity: 0, y: 30 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 1, delay: 0.3 }}
                      className="space-y-1 xl:space-y-2 font-archivo text-left"
                    >
                      {/* Line 1: THE OPERATING */}
                      <div className="text-4xl lg:text-5xl xl:text-6xl font-bold tracking-tight leading-none">
                        <span className="inline-block transform -skew-x-6 bg-gradient-to-r from-[#c7d2fe] to-[#a5b4fc] bg-clip-text text-transparent drop-shadow-lg xl:px-2">
                          THE OPERATING
                        </span>
                      </div>

                      {/* Line 2: SYSTEM FOR */}
                      <div className="text-4xl lg:text-5xl xl:text-6xl font-bold tracking-tight leading-none">
                        <span className="inline-block transform -skew-x-6 bg-gradient-to-r from-[#818cf8] to-[#6366f1] bg-clip-text text-transparent drop-shadow-lg xl:px-2">
                          SYSTEM FOR
                        </span>
                      </div>

                      {/* Line 3: INDEPENDENT MUSIC */}
                      <div className="text-4xl lg:text-5xl xl:text-6xl font-bold tracking-tight leading-none">
                        <span
                          className="inline-block transform -skew-x-6 bg-gradient-to-r from-[#FA6138] to-[#e55532] bg-clip-text text-transparent drop-shadow-lg xl:px-2"
                          style={{ filter: "drop-shadow(0 0 30px rgba(250, 97, 56, 0.5))" }}
                        >
                          INDEPENDENT
                        </span>
                        <span
                          className="inline-block transform -skew-x-6 bg-gradient-to-r from-[#e55532] to-[#FA6138] bg-clip-text text-transparent drop-shadow-lg xl:px-2"
                          style={{ filter: "drop-shadow(0 0 30px rgba(250, 97, 56, 0.5))" }}
                        >
                          MUSIC
                        </span>
                      </div>
                    </motion.h1>

                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.8, delay: 0.5 }}
                      className="text-lg xl:text-xl bg-gradient-to-r from-[#818cf8] via-[#6366f1] to-[#4f46e5] bg-clip-text text-transparent font-light tracking-[0.2em] whitespace-nowrap"
                    >
                      All the Power of a Label.<br /> None of the Compromise.
                    </motion.div>
                  </div>

                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.6 }}
                    className="space-y-1 xl:space-y-2"
                  >
                    <p className="bg-gradient-to-r from-[#a5b4fc]/80 via-[#818cf8]/85 to-[#6366f1]/80 bg-clip-text text-transparent text-sm md:text-base tracking-wide font-bold">
                      MARKETING &nbsp; · &nbsp; MASTERING &nbsp; · &nbsp; STRATEGY &nbsp; · &nbsp; ANALYTICS &nbsp; · <br />
                      FAN ENGAGEMENT &nbsp; · &nbsp; SYNC &nbsp; · &nbsp; PLAYLISTS &nbsp; · &nbsp; PRESS & PR &nbsp; ·
                    </p>
                  </motion.div>

                  {/* Enhanced Value Proposition with sophisticated blue glass effect */}
                  <motion.div
                    initial={{ opacity: 0, y: 25 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 1, delay: 0.7 }}
                    className="relative bg-gradient-to-br from-[#312e81]/15 via-[#1e1b4b]/10 to-[#0f0f23]/20 backdrop-blur-xl border border-[#6366f1]/20 rounded-2xl p-6 md:p-8 overflow-hidden"
                    style={{
                      boxShadow:
                        "0 20px 40px rgba(99, 102, 241, 0.25), inset 0 1px 0 rgba(165, 180, 252, 0.15)",
                    }}
                  >
                    {/* Subtle animated background pattern */}
                    <div className="absolute inset-0 opacity-30">
                      <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-[#4f46e5]/5 to-transparent rounded-2xl" />
                      <div className="absolute bottom-0 right-0 w-2/3 h-2/3 bg-gradient-to-tl from-[#6366f1]/8 to-transparent rounded-2xl" />
                    </div>

                    <div className="relative z-10">
                      <p className="text-[#c7d2fe] text-sm md:text-base leading-relaxed mb-6 font-light">
                        Built by artists who understand the struggle. Get the
                        power of a full label team while keeping 100% creative
                        and financial control.
                      </p>
                      <div className="flex flex-wrap items-center gap-x-8 gap-y-3 text-xs md:text-sm">
                        <div className="flex items-center text-[#c7d2fe] text-xl md:text-base leading-relaxed mb-6 font-bold">
                          <span><Brain className="w-8 h-8 text-[#FA6138] mr-4" /></span>
                          ALL POWERED BY OUR AMPLITUDE AI
                        </div>
                        {/* {[
                            {
                              feature: "🧠 Tailored Strategist",
                              color: "from-[#FA6138] to-[#6366f1]",
                            },
                            {
                              feature: "📊 Smart Fan Analytics",
                              color: "from-[#818cf8] to-[#FA6138]",
                            },
                            {
                              feature: "🚀 AI Campaign Launcher",
                              color: "from-[#4f46e5] to-[#e55532]",
                            },
                          ].map((item, index) => (
                            <span
                              key={index}
                              className="flex items-center text-[#a5b4fc]/90"
                            >
                              <span
                                className={`w-2 h-2 bg-gradient-to-r ${item.color} rounded-full mr-3 shadow-sm`}
                              ></span>
                              {item.feature}
                            </span>
                          ))} */}
                      </div>
                    </div>
                  </motion.div>

                  {/* Enhanced CTA Buttons */}
                  <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 1, delay: 0.8 }}
                    className="flex flex-col sm:flex-row gap-4 pt-4"
                  >
                    <button
                      onClick={handleSubscription}
                      className="inline-block bg-gradient-to-r from-[#FA6138] to-[#e55532] text-white text-center
                             px-5 py-4 text-xs font-medium tracking-wider rounded-xl hover:from-[#e55532] hover:to-[#FA6138]
                             transition-all duration-300 shadow-xl hover:shadow-2xl transform hover:scale-105
                             backdrop-blur-sm border border-white/10"
                      style={{
                        boxShadow: "0 10px 30px rgba(250, 97, 56, 0.3)",
                      }}
                    >
                      BECOME AN UPNCOMER
                    </button>
                    <button
                      onClick={handleShowDistribution}
                      className="inline-block bg-white text-theme_clr2 text-center
                             px-5 py-4 text-xs font-medium tracking-wider rounded-xl hover:from-[#e55532] hover:to-[#FA6138]
                             transition-all duration-300 shadow-xl hover:shadow-2xl transform hover:scale-105
                             backdrop-blur-sm border border-white/10"
                      style={{
                        boxShadow: "0 10px 30px rgba(250, 97, 56, 0.3)",
                      }}
                    >
                      DISTRIBUTE YOUR MUSIC
                    </button>
                    <a
                      href="https://www.instagram.com/upncomer.co/"
                      className="inline-flex items-center justify-center bg-[#1e3a8a]/20 border border-[#3b82f6]/30 text-[#93c5fd] 
                             px-5 py-4 text-xs font-medium tracking-wider rounded-xl hover:bg-[#3b82f6]/20 hover:border-[#60a5fa]/40
                             transition-all duration-300 backdrop-blur-xl shadow-lg hover:shadow-xl"
                    >
                      {/* <Play className="w-4 h-4 mr-2" /> */}
                      <span>FOLLOW THE BUILD <i class="ml-2 bi bi-wrench"></i></span>
                    </a>
                  </motion.div>
                </motion.div>
              </div>

              {/* Enhanced Laptop Animation with blue variations */}
              <div className="max-w-2xl w-full mx-auto lg:-mt-64">
                <motion.div
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 1, delay: 0.9 }}
                  className="relative"
                  style={{ willChange: "transform" }}
                >
                  {/* Enhanced Laptop Container */}
                  <div className="relative mx-auto max-w-[580px] min-h-[380px]">
                    {/* Laptop Base with blue accents */}
                    <div className="absolute bottom-0 w-full h-10 bg-gradient-to-r from-[#1e3a8a] via-[#0a1a8a] to-[#1e3a8a] rounded-b-2xl shadow-2xl border-t border-[#3b82f6]/20">
                      <div className="absolute bottom-1.5 left-1/2 transform -translate-x-1/2 w-20 h-4 bg-[#000456] rounded border border-[#1e3a8a]" />
                    </div>

                    {/* Enhanced Laptop Screen with blue glow */}
                    <motion.div
                      className="relative w-full h-80 bg-gradient-to-br from-[#0a1a8a] via-[#00067E] to-[#000456] rounded-t-2xl border-2 border-[#1e3a8a] shadow-2xl overflow-hidden"
                      animate={{
                        boxShadow: [
                          "0 0 40px rgba(59, 130, 246, 0.3), 0 0 80px rgba(250, 97, 56, 0.2)",
                          "0 0 60px rgba(96, 165, 250, 0.4), 0 0 100px rgba(250, 97, 56, 0.3)",
                          "0 0 40px rgba(59, 130, 246, 0.3), 0 0 80px rgba(250, 97, 56, 0.2)",
                        ],
                      }}
                      transition={{
                        duration: 4,
                        repeat: Number.POSITIVE_INFINITY,
                        ease: "easeInOut",
                      }}
                      style={{ willChange: "box-shadow" }}
                    >
                      {/* Enhanced Screen Content */}
                      <div className="absolute inset-1 bg-[#000456] rounded-xl overflow-hidden border border-[#1e3a8a]/30">
                        <motion.div
                          className="relative w-full h-full"
                          animate={{ opacity: [0.95, 1, 0.95] }}
                          transition={{
                            duration: 3,
                            repeat: Number.POSITIVE_INFINITY,
                            ease: "easeInOut",
                          }}
                          style={{ willChange: "opacity" }}
                        >
                          {/* Dashboard image enlarged and anchored from bottom */}
                          <div className="w-full h-full bg-[#000456] p-1 overflow-hidden">
                            <div className="relative w-full h-full flex items-end justify-center overflow-hidden">
                              <img
                                src="https://hebbkx1anhila5yf.public.blob.vercel-storage.com/Screenshot%202025-05-20%20at%2011.46.14%E2%80%AFpm-rK3WvHIErucUcAf9PF4sxtc2hhTExU.png"
                                alt="UPNCOMER Dashboard"
                                className="w-full max-h-none h-80 object-cover object-top rounded-lg transform origin-bottom pt-4"
                                style={{
                                  boxShadow: "0 0 10px rgba(0, 0, 0, 0.2)",
                                }}
                              />
                            </div>
                          </div>

                          {/* Enhanced Screen Effects with blue tint */}
                          <div className="absolute inset-0 bg-gradient-to-t from-transparent via-transparent to-[#3b82f6]/5 pointer-events-none rounded-xl" />
                          <motion.div
                            className="absolute inset-0 bg-gradient-to-br from-[#60a5fa]/5 to-transparent"
                            animate={{ opacity: [0.05, 0.15, 0.05] }}
                            transition={{
                              duration: 8,
                              repeat: Number.POSITIVE_INFINITY,
                              ease: "easeInOut",
                            }}
                          />
                        </motion.div>
                      </div>

                      {/* Enhanced Screen Bezel */}
                      <div className="absolute top-1.5 left-1/2 transform -translate-x-1/2 w-1.5 h-1.5 bg-[#000456] rounded-full border border-[#1e3a8a]">
                        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-0.5 h-0.5 bg-[#3b82f6]/60 rounded-full"></div>
                      </div>
                    </motion.div>

                    {/* Enhanced Laptop Shadow */}
                    <div className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-[95%] h-2 bg-[#000456]/40 rounded-full blur-sm" />
                  </div>

                  {/* Enhanced Floating Elements with blue variations */}
                  <motion.div
                    className="absolute -top-10 right-0 xl:-right-10 w-16 h-16 bg-gradient-to-br from-[#3b82f6]/25 to-[#FA6138]/15 rounded-full flex items-center justify-center backdrop-blur-xl border border-[#60a5fa]/30"
                    animate={{ y: [-15, 15, -15] }}
                    transition={{
                      duration: 5,
                      repeat: Number.POSITIVE_INFINITY,
                      ease: "easeInOut",
                    }}
                    style={{
                      willChange: "transform",
                      boxShadow: "0 10px 30px rgba(59, 130, 246, 0.3)",
                    }}
                  >
                    <TrendingUp className="w-8 h-8 text-[#93c5fd]" />
                  </motion.div>

                  <motion.div
                    className="absolute top-1/4 right-0 xl:-right-14 bg-[#1e3a8a]/25 backdrop-blur-xl border border-[#3b82f6]/30 rounded-xl px-4 py-3"
                    animate={{ y: [-8, 8, -8] }}
                    transition={{
                      duration: 4,
                      repeat: Number.POSITIVE_INFINITY,
                      ease: "easeInOut",
                      delay: 1,
                    }}
                    style={{
                      willChange: "transform",
                      boxShadow: "0 10px 30px rgba(30, 58, 138, 0.3)",
                    }}
                  >
                    <div className="text-[#FA6138] text-base font-bold">
                      +127%
                    </div>
                    <div className="text-[#93c5fd] text-sm">Growth</div>
                  </motion.div>

                  {/* Enhanced Bottom Left Elements with blue theme */}
                  <motion.div
                    className="absolute bottom-10 left-0 md:-left-14 w-14 h-14 bg-gradient-to-br from-[#60a5fa]/20 to-[#1e3a8a]/15 rounded-full flex items-center justify-center backdrop-blur-xl border border-[#3b82f6]/30"
                    animate={{ y: [-12, 12, -12] }}
                    transition={{
                      duration: 6,
                      repeat: Number.POSITIVE_INFINITY,
                      ease: "easeInOut",
                      delay: 2,
                    }}
                    style={{
                      willChange: "transform",
                      boxShadow: "0 10px 30px rgba(96, 165, 250, 0.2)",
                    }}
                  >
                    <BarChart3 className="w-7 h-7 text-[#93c5fd]" />
                  </motion.div>

                  <motion.div
                    className="absolute bottom-1/3 left-0 md:-left-18 bg-[#0a1a8a]/25 backdrop-blur-xl border border-[#1e3a8a]/30 rounded-xl px-4 py-3"
                    animate={{ y: [-9, 9, -9] }}
                    transition={{
                      duration: 5,
                      repeat: Number.POSITIVE_INFINITY,
                      ease: "easeInOut",
                      delay: 3,
                    }}
                    style={{
                      willChange: "transform",
                      boxShadow: "0 10px 30px rgba(10, 26, 138, 0.3)",
                    }}
                  >
                    <div className="text-[#FA6138] text-base font-bold">
                      +89%
                    </div>
                    <div className="text-[#60a5fa] text-sm">Revenue</div>
                  </motion.div>

                  {/* Enhanced Additional Element */}
                  <motion.div
                    className="absolute bottom-20 left-0 md:-left-10 w-12 h-12 bg-gradient-to-br from-[#FA6138]/20 to-[#3b82f6]/15 rounded-full flex items-center justify-center backdrop-blur-xl border border-[#60a5fa]/25"
                    animate={{
                      y: [-8, 8, -8],
                      rotate: [0, 360, 0],
                    }}
                    transition={{
                      y: {
                        duration: 7,
                        repeat: Number.POSITIVE_INFINITY,
                        ease: "easeInOut",
                        delay: 1.5,
                      },
                      rotate: {
                        duration: 25,
                        repeat: Number.POSITIVE_INFINITY,
                        ease: "linear",
                      },
                    }}
                    style={{
                      willChange: "transform",
                      boxShadow: "0 8px 25px rgba(59, 130, 246, 0.2)",
                    }}
                  >
                    <Zap className="w-6 h-6 text-[#93c5fd]" />
                  </motion.div>

                  {/* Enhanced Data Flow Indicators with blue gradients */}
                  <motion.div
                    className="absolute top-1/2 left-0 md:-left-8 w-4 h-4 bg-gradient-to-r from-[#FA6138] to-[#3b82f6] rounded-full"
                    animate={{
                      opacity: [0, 1, 0],
                      x: [-20, 20, -20],
                      scale: [0.8, 1.2, 0.8],
                    }}
                    transition={{
                      duration: 3,
                      repeat: Number.POSITIVE_INFINITY,
                      ease: "easeInOut",
                    }}
                    style={{
                      willChange: "transform, opacity",
                      boxShadow: "0 0 15px rgba(59, 130, 246, 0.6)",
                    }}
                  />

                  <motion.div
                    className="absolute top-1/3 left-0 md:-left-12 w-3 h-3 bg-gradient-to-r from-[#60a5fa] to-[#FA6138] rounded-full"
                    animate={{
                      opacity: [0, 1, 0],
                      x: [-25, 25, -25],
                      scale: [0.6, 1, 0.6],
                    }}
                    transition={{
                      duration: 4,
                      repeat: Number.POSITIVE_INFINITY,
                      ease: "easeInOut",
                      delay: 1.5,
                    }}
                    style={{
                      willChange: "transform, opacity",
                      boxShadow: "0 0 12px rgba(96, 165, 250, 0.5)",
                    }}
                  />
                </motion.div>
              </div>
            </div>
          </div>
        </main>

        {/* Enhanced Features Section with blue variations */}
        <section className="relative z-10 py-12 lg:py-14">
          <div className="container mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 1.2 }}
              className="text-center mb-14"
            >
              <h2 className="text-4xl font-bold bg-gradient-to-r from-[#c7d2fe] via-[#a5b4fc] to-[#818cf8] bg-clip-text text-transparent mb-2 drop-shadow-lg">
                Everything You Need to Succeed
              </h2>
              <p className="bg-gradient-to-r from-[#818cf8]/80 via-[#6366f1]/85 to-[#4f46e5]/80 bg-clip-text text-transparent text-lg font-light">
                Replace expensive industry services with AI-powered automation
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                {
                  icon: <TrendingUp className="w-8 h-8 text-[#FA6138]" />,
                  title: "Growth Engine",
                  description:
                    "Launch and optimize ad campaigns across all major platforms. AI-driven targeting and budget optimization for maximum ROI.",
                  value: "Replaces $3K/month agencies",
                  bgGradient:
                    "from-[#312e81]/20 via-[#1e1b4b]/15 to-[#0f0f23]/25",
                  borderColor: "border-[#6366f1]/25",
                  hoverBg: "hover:from-[#4f46e5]/15 hover:to-[#312e81]/20",
                },
                {
                  icon: <Users className="w-8 h-8 text-[#FA6138]" />,
                  title: "Artist Manager",
                  description:
                    "Track fans, manage smart links, and monitor revenue streams. Complete artist career management in one dashboard.",
                  value: "Replaces $2K/month management",
                  bgGradient:
                    "from-[#1e1b4b]/20 via-[#312e81]/15 to-[#4338ca]/10",
                  borderColor: "border-[#4f46e5]/20",
                  hoverBg: "hover:from-[#6366f1]/15 hover:to-[#1e1b4b]/20",
                },
                {
                  icon: <BarChart3 className="w-8 h-8 text-[#FA6138]" />,
                  title: "Data Analyst",
                  description:
                    "Real-time performance insights across all platforms. Advanced analytics to guide strategic decisions and growth.",
                  value: "Replaces $500/month tools",
                  bgGradient:
                    "from-[#4338ca]/15 via-[#3730a3]/12 to-[#312e81]/18",
                  borderColor: "border-[#818cf8]/20",
                  hoverBg: "hover:from-[#a5b4fc]/15 hover:to-[#4338ca]/20",
                },
                {
                  icon: <Mic className="w-8 h-8 text-[#FA6138]" />,
                  title: "Content Creator",
                  description:
                    "Generate and schedule content across social platforms. AI-powered content creation and automated posting.",
                  value: "Replaces $1K/month creators",
                  bgGradient:
                    "from-[#1e3a8a]/25 via-[#1e1b4b]/20 to-[#0f0f23]/30",
                  borderColor: "border-[#c7d2fe]/15",
                  hoverBg: "hover:from-[#818cf8]/15 hover:to-[#1e3a8a]/25",
                },
                {
                  icon: <Megaphone className="w-8 h-8 text-[#FA6138]" />,
                  title: "Publicist",
                  description:
                    "Playlist pitching and press outreach automation. Connect with curators and media outlets at scale.",
                  value: "Replaces $1.5K/month PR",
                  bgGradient:
                    "from-[#6366f1]/20 via-[#4f46e5]/15 to-[#4338ca]/20",
                  borderColor: "border-[#a5b4fc]/20",
                  hoverBg: "hover:from-[#c7d2fe]/15 hover:to-[#6366f1]/20",
                },
                {
                  icon: <Headphones className="w-8 h-8 text-[#FA6138]" />,
                  title: "UrStudio",
                  description:
                    "A full mixing and mastering engineer at your fingertips. Split stems, analyze your tracks, enhance your mixes, and get release-ready sound — anytime.",
                  value: "Saves $200+ per track",
                  bgGradient:
                    "from-[#0f0f23]/30 via-[#1e1b4b]/25 to-[#312e81]/20",
                  borderColor: "border-[#4f46e5]/25",
                  hoverBg: "hover:from-[#4f46e5]/15 hover:to-[#0f0f23]/30",
                },
                {
                  icon: <Brain className="w-8 h-8 text-[#FA6138]" />,
                  title: "Amplitude AI",
                  description:
                    "Our intelligence layer that automates everything. Machine learning that optimizes your entire music career.",
                  value: "Replaces entire teams",
                  bgGradient:
                    "from-[#3730a3]/20 via-[#4338ca]/15 to-[#6366f1]/18",
                  borderColor: "border-[#818cf8]/25",
                  hoverBg: "hover:from-[#a5b4fc]/15 hover:to-[#3730a3]/20",
                },
                {
                  icon: <GraduationCap className="w-8 h-8 text-[#FA6138]" />,
                  title: "ProfessorU",
                  description:
                    "Learning built into the platform. Continuous education and skill development integrated into your workflow.",
                  value: "Replaces $300/month courses",
                  bgGradient:
                    "from-[#312e81]/25 via-[#1e1b4b]/20 to-[#3730a3]/22",
                  borderColor: "border-[#6366f1]/20",
                  hoverBg: "hover:from-[#818cf8]/15 hover:to-[#312e81]/25",
                },
              ].map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 1.4 + index * 0.1 }}
                  className={`bg-gradient-to-br ${feature.bgGradient} ${feature.hoverBg} backdrop-blur-xl border ${feature.borderColor} hover:border-[#a5b4fc]/30 rounded-2xl p-6 transition-all duration-500 group overflow-hidden relative cursor-pointer`}
                  style={{
                    boxShadow:
                      "0 10px 30px rgba(99, 102, 241, 0.15), inset 0 1px 0 rgba(165, 180, 252, 0.1)",
                  }}
                  onClick={() => navigateToFeatures(index)}
                >
                  {/* Subtle animated background overlay */}
                  <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                    <div className="absolute top-0 right-0 w-1/2 h-1/2 bg-gradient-to-bl from-[#6366f1]/10 to-transparent rounded-2xl" />
                    <div className="absolute bottom-0 left-0 w-1/3 h-1/3 bg-gradient-to-tr from-[#4f46e5]/8 to-transparent rounded-2xl" />
                  </div>

                  <div className="relative z-10">
                    <div className="mb-4 transform group-hover:scale-110 transition-transform duration-300">
                      {feature.icon}
                    </div>
                    <h3 className="text-xl font-semibold bg-gradient-to-r from-[#c7d2fe] to-[#a5b4fc] bg-clip-text text-transparent mb-3">
                      {feature.title} {(feature.title === "Publicist" || feature.title === "Content Creator") && <i class="ml-2 bi bi-wrench"></i>}
                    </h3>
                    <p className="text-[#818cf8]/80 mb-4 font-light leading-relaxed">
                      {feature.description}
                    </p>
                    <div className="text-xs text-[#FA6138] font-medium tracking-wide">
                      {feature.value}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Enhanced Social Proof with smooth transition to footer */}
        <section className="relative z-10 py-12 lg:py-14"
          style={{
            background:
              "linear-gradient(to bottom, transparent 0%, rgba(0, 4, 86, 0.2) 50%, rgba(0, 6, 126, 0.4) 100%)",
            boxShadow: "inset 0 -100px 100px -100px rgba(0, 6, 126, 0.5)",
          }}
        >
          <div className="container mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 1.8 }}
            >
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.8 }}
                className="text-center"
              >
                <h2 className="text-3xl font-bold text-[#c7d2fe] mb-6">Ready to Build Your Music Empire?</h2>
                <p className="text-[#818cf8]/80 mb-8 max-w-2xl mx-auto">
                  Join hundreds of independent artists who are already growing their careers with UpNComer.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 mb-24 justify-center">
                  <button
                    onClick={handleSubscription}
                    className="inline-block bg-gradient-to-r from-[#FA6138] to-[#e55532] text-white text-center
                         px-8 py-4 text-sm font-medium tracking-wider rounded-xl hover:from-[#e55532] hover:to-[#FA6138]
                         transition-all duration-300 shadow-xl hover:shadow-2xl transform hover:scale-105"
                  >
                    BECOME AN UPNCOMER
                  </button>
                  <button
                    onClick={handleShowDistribution}
                    className="inline-block bg-white text-theme_clr2 text-center
                             px-8 py-4 text-sm font-medium tracking-wider rounded-xl hover:from-[#e55532] hover:to-[#FA6138]
                             transition-all duration-300 shadow-xl hover:shadow-2xl transform hover:scale-105
                             backdrop-blur-sm border border-white/10"
                    style={{
                      boxShadow: "0 10px 30px rgba(250, 97, 56, 0.3)",
                    }}
                  >
                    DISTRIBUTE YOUR MUSIC
                  </button>
                  <a
                    href="https://www.instagram.com/upncomer.co/"
                    className="inline-flex items-center justify-center bg-[#1e3a8a]/20 border border-[#3b82f6]/30 text-[#93c5fd] 
                         px-8 py-4 text-sm font-medium tracking-wider rounded-xl hover:bg-[#3b82f6]/20 hover:border-[#60a5fa]/40
                         transition-all duration-300 backdrop-blur-xl shadow-lg hover:shadow-xl"
                  >
                    {/* <Play className="w-4 h-4 mr-2" /> */}
                    <span>FOLLOW THE BUILD <i class="ml-2 bi bi-wrench"></i></span>
                  </a>
                </div>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 2.3 }}
                className="bg-[#1e3a8a]/15 backdrop-blur-xl border border-[#3b82f6]/20 rounded-2xl p-8 max-w-2xl mt-6 mx-auto"
                style={{
                  boxShadow:
                    "0 20px 40px rgba(30, 58, 138, 0.3), inset 0 1px 0 rgba(59, 130, 246, 0.2)",
                }}
              >
                <div className="flex items-center justify-center mb-6">
                  <img
                    src={require("../assets/images/karan.png")}
                    alt="karan"
                    className="w-16 h-16 rounded-full border-2 border-[#3b82f6]/30 mr-4 shadow-lg object-cover"
                  />
                  <div className="text-left">
                    <div className="text-[#FA6138] font-medium text-lg">
                      karan
                    </div>
                    <div className="text-[#93c5fd]/80 text-sm font-light">
                      Independent Artist
                    </div>
                    <div className="text-xs text-[#60a5fa]/60">
                      100k monthly listeners
                    </div>
                  </div>
                </div>
                <p className="text-[#93c5fd] italic mb-4 font-light leading-relaxed">
                  "UpNComer replaced my entire marketing team. My streams grew
                  400% in 3 months and I kept 100% of my royalties. This is
                  the future of independent music."
                </p>
              </motion.div>
            </motion.div>
          </div>

          {/* Additional gradient overlay for smoother transition */}
          <div
            className="absolute left-0 right-0 bottom-0 h-32 z-0 pointer-events-none"
            style={{
              background:
                "linear-gradient(to bottom, transparent, rgba(0, 6, 126, 0.3) 50%, rgba(0, 4, 86, 0.6) 100%)",
            }}
          ></div>
        </section>
      </div>
      {/* Distribution Modal */}
      <DistributionModal
        isOpen={showDistributionModal}
        onClose={handleShowDistribution}
      />
      <PricingPlanModal isOpen={showPricingPlanModal} onClose={() => setShowPricingPlanModal(false)} returnUrl={window.location.origin + "/dashboard"} />
    </>
  );
}
